// @ts-nocheck
import { installDomTranslationGuard } from './utils/domTranslationGuard';
// Before anything renders: keep Chrome auto-translate (and other DOM-mutating
// extensions) from crashing React with removeChild/insertBefore NotFoundErrors
// — the crash non-English organizers hit repeatedly during onboarding.
installDomTranslationGuard();
import React from 'react'
import * as Sentry from "@sentry/react";
import ReactDOM from 'react-dom/client';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Root from './Root.jsx'
import './index.css'
import { BrowserRouter, RouterProvider } from "react-router-dom";
import { ThemeProvider as AppThemeProvider } from './contexts/ThemeContext';
import AppErrorFallback from './components/ErrorPage/AppErrorFallback';

Sentry.init({
  dsn: "https://3678c6d8ae3b03c5acb7e704c7cc37af@o4506977987985408.ingest.us.sentry.io/4509667575595008",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // __STATY_PROD_BUILD__ (vite.config define, keyed off `vite build`), NOT
  // process.env.NODE_ENV and NOT import.meta.env.PROD: both derive from
  // NODE_ENV, which the local .env AND the Cloudflare Pages build env set to
  // a non-production value — prod builds shipped with
  // environment:"development" twice (found 2026-10-06, again 2026-10-12), so
  // every real dash.staty.io error hid among dev noise.
  environment: __STATY_PROD_BUILD__ ? 'production' : 'development',
  // Performance Monitoring: all pageloads traced in dev; sampled in prod so
  // transactions don't burn the error quota.
  tracesSampleRate: __STATY_PROD_BUILD__ ? 0.1 : 1.0,
  profilesSampleRate: __STATY_PROD_BUILD__ ? 0 : 1.0,
});

// Create a theme that integrates with our CSS variables
const createAppTheme = () => createTheme({
  typography: {
    // Match the app-wide Inter stack (--font-sans in index.css) — without this
    // MUI falls back to Roboto/Helvetica, which we don't load, so MUI surfaces
    // rendered in a different font than the rest of the admin.
    fontFamily: '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
    button: {
      // MUI's default ALL-CAPS buttons clash with the shadcn buttons used
      // everywhere else in the app.
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  palette: {
    mode: 'light', // This will be overridden by CSS variables
    background: {
      default: 'hsl(var(--background))',
      paper: 'hsl(var(--card))',
    },
    text: {
      primary: 'hsl(var(--foreground))',
      secondary: 'hsl(var(--muted-foreground))',
    },
    primary: {
      main: 'hsl(var(--primary))',
      contrastText: 'hsl(var(--primary-foreground))',
    },
    secondary: {
      main: 'hsl(var(--secondary))',
      contrastText: 'hsl(var(--secondary-foreground))',
    },
    error: {
      main: 'hsl(var(--destructive))',
      contrastText: 'hsl(var(--destructive-foreground))',
    },
    divider: 'hsl(var(--border))',
    action: {
      hover: 'hsl(var(--accent))',
      selected: 'hsl(var(--accent))',
    },
  },
  // Align MUI geometry with the shadcn tokens so MUI and shadcn controls can
  // sit side by side: same 8px radius (--radius: 0.5rem), no paper gradient,
  // no ripple-heavy elevation jumps on buttons.
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'hsl(var(--muted))',
            color: 'hsl(var(--foreground))',
          },
          // Match the shadcn table's overline header treatment (ui/table.tsx)
          // so both table systems read identically.
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'hsl(var(--muted-foreground))',
          },
          '& .MuiDataGrid-columnHeader': {
            color: 'hsl(var(--foreground))',
            transition: 'all 0.2s ease',
            position: 'relative',
            '&:hover': {
              backgroundColor: 'hsl(var(--accent))',
              color: 'hsl(var(--accent-foreground))',
              cursor: 'pointer',
            },
            '&:focus': {
              backgroundColor: 'hsl(var(--accent))',
              color: 'hsl(var(--accent-foreground))',
              outline: '2px solid hsl(var(--ring))',
              outlineOffset: '-2px',
            },
            '&.Mui-selected': {
              backgroundColor: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
              fontWeight: 600,
            },
            '&[aria-selected="true"]': {
              backgroundColor: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
              fontWeight: 600,
            },
            '&[aria-sort="ascending"], &[aria-sort="descending"]': {
              backgroundColor: 'hsl(var(--accent))',
              color: 'hsl(var(--accent-foreground))',
              fontWeight: 500,
            },
          },
          '& .MuiDataGrid-cell': {
            color: 'hsl(var(--foreground))',
            transition: 'all 0.2s ease',
            position: 'relative',
            '&:hover': {
              backgroundColor: 'hsl(var(--muted))',
              cursor: 'pointer',
            },
            '&[tabindex="0"]': {
              backgroundColor: 'hsl(var(--muted))',
              color: 'hsl(var(--foreground))',
              outline: '1px solid hsl(var(--ring))',
              outlineOffset: '-1px',
              zIndex: 1,
              boxShadow: 'inset 0 0 0 1px hsl(var(--ring))',
            },
            '&:active': {
              backgroundColor: 'hsl(var(--accent))',
              transform: 'scale(0.99)',
            },
            '&.MuiDataGrid-cell--editing': {
              backgroundColor: 'hsl(var(--accent))',
              color: 'hsl(var(--foreground))',
              boxShadow: '0 0 0 1px hsl(var(--ring))',
              zIndex: 2,
            },
          },
          '& .MuiDataGrid-columnSeparator': {
            color: 'hsl(var(--border))',
            opacity: 0.3,
          },
          '& .MuiDataGrid-columnSeparator--sideRight': {
            borderRight: '1px solid hsl(var(--border))',
            opacity: 0.3,
          },
          '& .MuiDataGrid-columnSeparator--resizing': {
            color: 'hsl(var(--primary))',
            opacity: 0.8,
          },
        },
      },
    },
  },
});

const theme = createAppTheme();

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    {/* App-level crash net: any render error below shows the branded error
        page (with the Sentry event id) instead of unmounting to a blank page. */}
    <Sentry.ErrorBoundary
      fallback={({ eventId, resetError }) => (
        <AppErrorFallback eventId={eventId} resetError={resetError} />
      )}
    >
      <AppThemeProvider>
        <ThemeProvider theme={theme}>
          {/* <BrowserRouter context={{ test: "hello" }}> */}
            {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
            <CssBaseline />
            <Root />
          {/* </BrowserRouter> */}
        </ThemeProvider>
      </AppThemeProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
